import { useState, useEffect, useCallback } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { RefreshCw, Sparkles, CheckCircle2, Download } from "lucide-react";

/**
 * Header badge that ALWAYS shows the current app version.
 * - When user is on latest version: shows version in green ("✓")
 * - When new version is available: shows a pulsing emerald badge with "تحديث متاح"
 * - Clicking it opens a popover with version info + "تحديث الآن" button
 *   that clears all caches/SW and hard-reloads.
 */
export default function VersionBadge({ compact = false }) {
  const [serverVersion, setServerVersion] = useState(null);
  const [serverDate, setServerDate] = useState(null);
  const [changes, setChanges] = useState([]);
  const [installedVersion, setInstalledVersion] = useState(() => {
    try {
      return localStorage.getItem("app_version") || null;
    } catch {
      return null;
    }
  });

  const updateAvailable =
    !!serverVersion && !!installedVersion && installedVersion !== serverVersion;

  const fetchVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setServerVersion(data.version || null);
        setServerDate(data.date || null);
        setChanges(Array.isArray(data.changes) ? data.changes : []);
        // First-time visit — pin the installed version so the badge starts as "up to date"
        const stored = localStorage.getItem("app_version");
        if (!stored && data.version) {
          localStorage.setItem("app_version", data.version);
          setInstalledVersion(data.version);
        }
      }
    } catch {
      // silent — keep last known
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    const id = setInterval(fetchVersion, 60_000); // recheck every minute
    return () => clearInterval(id);
  }, [fetchVersion]);

  const handleUpdate = useCallback(() => {
    // Clear all caches
    if ("caches" in window) {
      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
    }
    // Skip-waiting بدلاً من unregister (آمن على Android WebApk)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => {
          r.update();
          if (r.waiting) {
            r.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
    }
    // Pin new version
    if (serverVersion) {
      try { localStorage.setItem("app_version", serverVersion); } catch {}
    }
    try { localStorage.removeItem("update_dismissed_at"); } catch {}
    // Hard reload bypassing cache (with delay to let SW activate)
    setTimeout(() => {
      window.location.href = window.location.href.split("?")[0] + "?v=" + Date.now();
    }, 800);
  }, [serverVersion]);

  // Display version (prefer server)
  const display = serverVersion || installedVersion || "—";

  // Styling
  const baseBtn = compact ? "h-9 px-2 gap-1" : "h-9 px-3 gap-1.5";
  const colors = updateAvailable
    ? "bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25"
    : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="version-badge-btn"
          className={`relative border ${colors} ${baseBtn} font-mono text-xs`}
          title={updateAvailable ? `تحديث متاح: ${serverVersion}` : `الإصدار ${display}`}
        >
          {updateAvailable ? (
            <>
              <span className="relative flex items-center">
                <Download className="w-3.5 h-3.5" />
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
              </span>
              {!compact && <span className="font-semibold">تحديث</span>}
              <span className="font-bold tabular-nums" data-testid="version-badge-number">
                {display}
              </span>
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5" />
              {!compact && <span>الإصدار</span>}
              <span className="font-bold tabular-nums" data-testid="version-badge-number">
                {display}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-80 bg-slate-900 border-slate-700 text-slate-100 p-0 overflow-hidden"
        data-testid="version-popover"
      >
        {/* Header */}
        <div
          className={`p-4 ${
            updateAvailable
              ? "bg-gradient-to-l from-amber-600 to-orange-600"
              : "bg-gradient-to-l from-emerald-600 to-teal-600"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white/80 text-xs">
                {updateAvailable ? "تحديث جديد متاح" : "أنت على آخر إصدار"}
              </div>
              <div className="text-white font-bold text-2xl tabular-nums" data-testid="version-popover-number">
                {display}
              </div>
              {serverDate && (
                <div className="text-white/70 text-xs mt-0.5">{serverDate}</div>
              )}
            </div>
            <div className="bg-white/20 p-2.5 rounded-lg">
              {updateAvailable ? (
                <Download className="w-6 h-6 text-white" />
              ) : (
                <CheckCircle2 className="w-6 h-6 text-white" />
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {installedVersion && installedVersion !== serverVersion && (
            <div className="text-xs text-slate-400 flex items-center justify-between border-b border-slate-700 pb-2">
              <span>إصدارك الحالي:</span>
              <span className="font-mono text-slate-200">{installedVersion}</span>
            </div>
          )}

          {changes.length > 0 && (
            <div>
              <div className="text-xs text-slate-400 mb-1.5 font-semibold">ما الجديد:</div>
              <ul className="text-sm space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {changes.slice(0, 6).map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-200">
                    <span className="text-emerald-400 mt-0.5 shrink-0">•</span>
                    <span className="leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={handleUpdate}
            data-testid="version-update-now-btn"
            className={`w-full font-bold ${
              updateAvailable
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : "bg-slate-700 hover:bg-slate-600 text-white"
            }`}
          >
            <RefreshCw className="w-4 h-4 me-2" />
            {updateAvailable ? "تحديث إلى آخر إصدار الآن" : "إعادة تحميل التطبيق"}
          </Button>

          <p className="text-[10px] text-slate-500 text-center leading-relaxed">
            يمسح الكاش ويُعيد تحميل التطبيق من السيرفر
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
